/**
 * src/lib/ga4.ts
 *
 * Google Analytics 4 — carregamento dinâmico após consent (LGPD).
 *
 * Comportamento:
 *  - Lê `VITE_GA4_ID` do env. Sem ID, no-op.
 *  - Carrega gtag.js APENAS quando cookie consent === "accepted".
 *  - Reage a mudanças de consent: aceita depois → carrega; revoga → no-op
 *    para próximos eventos (script permanece, mas `gaEvent` para de enviar).
 *  - `gaEvent(name, params)` é fire-and-forget e nunca lança.
 *
 * Eventos canônicos mapeados (Bloco 2 lançamento):
 *  - signup_completed
 *  - onboarding_completed
 *  - first_checkin
 *  - purchase
 *
 * Demais eventos passam por aqui também (mirror do analytics interno).
 */

import {
  getCookieConsentStatus,
  isCookieConsentAccepted,
  onCookieConsentChange,
} from "@/lib/cookieConsent";

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

// O ID de medição é público por natureza (vai no HTML/servidor do Google).
// Fallback literal porque o .env de build nem sempre carrega variáveis extras.
const GA4_ID =
  (import.meta.env.VITE_GA4_ID as string | undefined) || "G-LHDSEG8KBT";

let loaded = false;
let initWatcherStarted = false;

/**
 * Fila de eventos disparados ANTES do gtag.js existir.
 *
 * A observabilidade só carrega depois do `load`, e a barra de download monta
 * antes disso — sem a fila, o `app_banner_shown` era descartado em silêncio
 * (`if (!window.gtag) return`). Teto pequeno para não crescer sem limite.
 */
type EventoNaFila = { name: string; params: Record<string, unknown> };
const fila: EventoNaFila[] = [];
const FILA_MAX = 30;

function esvaziarFila(): void {
  if (!window.gtag || !isCookieConsentAccepted()) return;
  while (fila.length) {
    const ev = fila.shift()!;
    try {
      window.gtag("event", ev.name, ev.params);
    } catch {
      /* never throw */
    }
  }
}

function loadGtag(): void {
  if (loaded || typeof window === "undefined" || !GA4_ID) return;
  loaded = true;

  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`;
  document.head.appendChild(s);

  window.dataLayer = window.dataLayer || [];
  // ATENÇÃO: o gtag.js só processa entradas que são o objeto `arguments`.
  // Empurrar um array (`push(args)`) faz o `config` ficar no dataLayer sem
  // nunca ser executado — o script carrega e NADA é enviado, em silêncio.
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  };
  window.gtag("js", new Date());
  window.gtag("config", GA4_ID, {
    anonymize_ip: true,
    send_page_view: true,
  });

  esvaziarFila();
}

/** Init: chame no main.tsx. Carrega só se consent já aceito; senão arma watcher. */
export function initGA4(): void {
  if (!GA4_ID || typeof window === "undefined") return;
  // Apple Guideline 5.1.2(i): no tracking cookies em app nativo sem ATT.
  // Native (Capacitor iOS/Android) nunca carrega GA4.
  try {
    const cap = (window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor;
    if (cap?.isNativePlatform?.()) return;
  } catch { /* ignore */ }
  if (isCookieConsentAccepted()) {
    loadGtag();
  }
  if (!initWatcherStarted) {
    initWatcherStarted = true;
    onCookieConsentChange((status) => {
      if (status === "accepted") {
        loadGtag();
        // `loadGtag` já esvazia na primeira vez; aqui cobre o caso de o script
        // já estar carregado e a fila ter nascido antes da decisão.
        esvaziarFila();
        return;
      }
      // Recusado (ou decisão apagada): nada do que ficou na espera pode sair.
      if (status === "rejected") fila.length = 0;
    });
  }
}

/**
 * Forward de evento pro GA4.
 *
 * Três estados de consentimento, e a diferença entre eles é o que fazia o
 * `app_banner_shown` nunca chegar ao GA4:
 *  - **aceito**: envia (ou enfileira, se o gtag ainda não subiu);
 *  - **ainda não decidido** (banner de cookies na tela): ENFILEIRA. A barra de
 *    download monta junto com a página, antes de a visitante aceitar — e o
 *    evento era descartado em silêncio, sem nunca ser tentado de novo, porque
 *    a exibição já tinha sido marcada como medida na sessão;
 *  - **recusado**: descarta, e a fila é limpa no momento da recusa.
 *
 * Nada sai antes do "aceito" — a fila só é esvaziada quando a decisão chega.
 *
 * `transport_type: 'beacon'` em tudo: quando o evento é seguido de navegação
 * (clique em "Instalar", redirecionamento do /app), a requisição normal é
 * cancelada na saída da página; o beacon sobrevive.
 */
export function gaEvent(
  name: string,
  params?: Record<string, unknown>,
): void {
  try {
    if (!GA4_ID || typeof window === "undefined") return;
    const consentimento = getCookieConsentStatus();
    if (consentimento === "rejected") return;
    const carga = { transport_type: "beacon", ...(params ?? {}) };
    if (consentimento === null || !window.gtag) {
      if (fila.length < FILA_MAX) fila.push({ name, params: carga });
      return;
    }
    window.gtag("event", name, carga);
  } catch {
    /* never throw */
  }
}
